import { useEffect, useState } from "react";
import { AudioLines, ListTree } from "lucide-react";
import { api } from "../lib/api";
import WhisperRequestAudit from "./WhisperRequestAudit";

const usd = (v, dp = 6) =>
  v == null ? "—" : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: dp })}`;
const mins = (v) => (v == null ? "—" : `${Number(v).toLocaleString("en-US", { maximumFractionDigits: 2 })}m`);
const pct = (v) => (v == null ? "—" : `${Number(v).toFixed(1)}%`);

/** Read-only diagnostic. Derived purely from Whisper cost telemetry that already exists. */
export default function WhisperOverlapAudit({ rangeQuery }) {
  const [data, setData] = useState(null);
  const [requestsJob, setRequestsJob] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: d } = await api.get(`/admin/spend/whisper-audit?${rangeQuery}`);
        if (alive) setData(d);
      } catch {
        if (alive) setData(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [rangeQuery]);

  if (!data) return null;
  const t = data.totals || {};
  const c = data.config || {};

  return (
    <section data-testid="whisper-audit-section">
      <h2 className="font-heading text-2xl font-bold tracking-tight mb-3 flex items-center gap-2">
        <AudioLines className="w-4 h-4 text-blue-400" /> Whisper overlap audit
      </h2>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-3">
        {[
          { label: "Audio submitted", value: mins(t.submitted_minutes), tid: "whisper-audit-submitted" },
          { label: "Video duration", value: mins(t.media_minutes), tid: "whisper-audit-media" },
          { label: "Submitted / duration", value: t.submitted_ratio ? `${t.submitted_ratio}×` : "—", tid: "whisper-audit-ratio" },
          {
            label: "Intentional chunk overlap",
            value: `${mins(t.intentional_overlap_minutes)} · ${pct(t.intentional_overlap_pct_of_submitted)}`,
            tid: "whisper-audit-intentional",
          },
          {
            label: "Recovery / retry re-transcription",
            value: `${mins(t.recovery_retry_minutes)} · ${pct(t.recovery_retry_pct_of_submitted)}`,
            tid: "whisper-audit-recovery",
          },
        ].map((k) => (
          <div key={k.label} className="border border-zinc-800 bg-zinc-900/40 p-4" data-testid={k.tid}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{k.label}</div>
            <div className="text-xl font-mono text-white mt-1">{k.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
        {[
          {
            label: "Total repeated audio",
            value: `${mins(t.overlap_minutes)} · ${pct(t.overlap_pct)} of submitted`,
            tid: "whisper-audit-overlap",
          },
          { label: "Intentional overlap cost", value: usd(t.intentional_overlap_cost_usd, 4), tid: "whisper-audit-intentional-cost" },
          { label: "Recovery / retry cost", value: usd(t.extra_overlap_cost_usd, 4), tid: "whisper-audit-recovery-cost" },
        ].map((k) => (
          <div key={k.label} className="border border-zinc-800 bg-zinc-900/40 p-4" data-testid={k.tid}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{k.label}</div>
            <div className="text-xl font-mono text-white mt-1">{k.value}</div>
          </div>
        ))}
      </div>

      <ul className="text-[11px] font-mono text-zinc-500 mb-3 space-y-1" data-testid="whisper-audit-interpretation">
        {(data.interpretation || []).map((i) => (
          <li key={i.slice(0, 24)}>· {i}</li>
        ))}
      </ul>

      <p className="text-[11px] font-mono text-zinc-500 mb-3" data-testid="whisper-audit-config-note">
        Chunk window {c.chunk_seconds}s with {c.chunk_overlap_seconds}s overlap accounts for{" "}
        {pct(c.expected_overlap_pct_of_submitted)} of submitted audio ({c.expected_submitted_ratio}× duration).
        Measured {t.submitted_ratio ? `${t.submitted_ratio}×` : "—"} · beyond the chunk window:{" "}
        {mins(t.extra_overlap_minutes)} ({usd(t.extra_overlap_cost_usd, 4)}) from gap recovery, hole
        re-transcription and retries. Read-only: transcription behaviour is untouched.
        {t.jobs_insufficient_data
          ? ` ${t.jobs_insufficient_data} job(s) lack the telemetry needed to audit — not estimated.`
          : ""}
      </p>

      <div className="border border-zinc-800 overflow-x-auto" data-testid="whisper-audit-table">
        <table className="w-full text-xs">
          <thead className="bg-zinc-900/60 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left px-3 py-3">Job</th>
              <th className="text-right px-3 py-3">Requests</th>
              <th className="text-right px-3 py-3">Submitted</th>
              <th className="text-right px-3 py-3">Duration</th>
              <th className="text-right px-3 py-3">Ratio</th>
              <th className="text-right px-3 py-3">Intentional overlap</th>
              <th className="text-right px-3 py-3">Recovery / retry</th>
              <th className="text-right px-3 py-3">Total repeated</th>
              <th className="text-right px-3 py-3">Repeat cost</th>
              <th className="text-right px-3 py-3">Whisper cost</th>
              <th className="text-right px-3 py-3">Per request</th>
            </tr>
          </thead>
          <tbody>
            {(data.rows || []).map((r) => (
              <tr
                key={r.job_id}
                className="border-t border-zinc-800/60 hover:bg-zinc-900/40"
                data-testid={`whisper-audit-row-${r.job_id}`}
              >
                <td className="px-3 py-2.5 font-mono text-zinc-300">{r.job_id.slice(0, 16)}</td>
                <td className="px-3 py-2.5 text-right text-zinc-400">{r.requests}</td>
                <td className="px-3 py-2.5 text-right text-zinc-400">{mins(r.submitted_minutes)}</td>
                <td className="px-3 py-2.5 text-right text-zinc-400">{mins(r.media_minutes)}</td>
                <td className="px-3 py-2.5 text-right text-zinc-400">
                  {r.submitted_ratio ? `${r.submitted_ratio}×` : "—"}
                </td>
                <td className="px-3 py-2.5 text-right text-zinc-400">
                  {mins(r.intentional_overlap_minutes)}
                  {r.intentional_overlap_pct_of_submitted != null && (
                    <span className="block text-[10px] text-zinc-600">
                      {pct(r.intentional_overlap_pct_of_submitted)}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right text-amber-300">
                  {mins(r.recovery_retry_minutes)}
                  {r.recovery_retry_pct_of_submitted != null && (
                    <span className="block text-[10px] text-zinc-600">
                      {pct(r.recovery_retry_pct_of_submitted)}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right text-zinc-400">
                  {mins(r.overlap_minutes)}
                  {r.overlap_pct != null && (
                    <span className="block text-[10px] text-zinc-600">{pct(r.overlap_pct)}</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-white">{usd(r.overlap_cost_usd, 4)}</td>
                <td className="px-3 py-2.5 text-right font-mono text-zinc-400">
                  {usd(r.whisper_cost_usd, 4)}
                  {!r.data_sufficient && (
                    <span className="block text-[10px] text-zinc-600">{r.data_note}</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right">
                  <button
                    type="button"
                    onClick={() => setRequestsJob(r.job_id)}
                    data-testid={`whisper-audit-requests-${r.job_id}`}
                    className="text-[10px] font-mono uppercase tracking-widest border border-zinc-800 hover:border-blue-500/50 hover:text-blue-300 px-2 py-1 inline-flex items-center gap-1"
                  >
                    <ListTree className="w-3 h-3" /> Requests
                  </button>
                </td>
              </tr>
            ))}
            {(data.rows || []).length === 0 && (
              <tr>
                <td colSpan={11} className="text-center text-zinc-500 py-6" data-testid="whisper-audit-empty">
                  No Whisper telemetry in this range.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {requestsJob && (
        <WhisperRequestAudit jobId={requestsJob} onClose={() => setRequestsJob(null)} />
      )}
    </section>
  );
}
